// AssetModal — full-screen overlay showing a synthesized artifact (brief, timeline, etc.).
// Includes copy-to-clipboard, download .md, and (placeholder) share.

import { useState } from 'react';
import type { ThreadAsset, AssetType } from '../types';
import { Markdown } from './Markdown';

interface Props {
  asset: ThreadAsset | null;
  generating: AssetType | null;
  error: string | null;
  onClose: () => void;
}

const ASSET_LABEL: Record<AssetType, string> = {
  brief: 'Briefing',
  timeline: 'Timeline',
  flowchart: 'Flowchart',
  workflow: 'Workflow',
  notes: 'Compiled Notes',
  report: 'Technical Report',
  supply_map: 'Supply Map',
};

export function AssetModal({ asset, generating, error, onClose }: Props) {
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const visible = !!asset || !!generating || !!error;
  if (!visible) return null;

  function copy() {
    if (!asset) return;
    navigator.clipboard.writeText(asset.content_markdown);
    setCopyState('copied');
    setTimeout(() => setCopyState('idle'), 1500);
  }

  function downloadMarkdown() {
    if (!asset) return;
    const blob = new Blob([asset.content_markdown], { type: 'text/markdown' });
    triggerDownload(blob, `${slugifyTitle(asset.title)}.md`);
  }

  function downloadDocx() {
    if (!asset) return;
    const html = renderMarkdownForDocx(asset.content_markdown, asset.title);
    // Word recognizes HTML wrapped with the right MIME + .doc extension.
    const blob = new Blob([html], { type: 'application/msword' });
    triggerDownload(blob, `${slugifyTitle(asset.title)}.doc`);
  }

  function downloadPdf() {
    if (!asset) return;
    const html = renderMarkdownForPrint(asset.content_markdown, asset.title);
    const w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    // Give the new window a beat to lay out before invoking print
    setTimeout(() => { w.focus(); w.print(); }, 250);
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl max-h-[90vh] bg-card border border-border rounded-xl shadow-lg overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-hairline flex items-center justify-between gap-4">
          <div>
            <div className="font-display text-[10px] uppercase tracking-wide12 text-text3">
              {asset ? 'Synthesized asset' : generating ? 'Generating' : 'Error'}
            </div>
            <div className="font-display text-lg text-ink mt-0.5">
              {asset ? asset.title : generating ? ASSET_LABEL[generating] : 'Failed'}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {asset && (
              <>
                <button
                  onClick={copy}
                  className="text-[11px] font-medium px-3 py-1.5 rounded-md border border-border hover:border-ink/40 transition-colors text-text2 hover:text-ink"
                >
                  {copyState === 'copied' ? 'Copied' : 'Copy'}
                </button>
                <button
                  onClick={downloadMarkdown}
                  className="text-[11px] font-medium px-3 py-1.5 rounded-md border border-border hover:border-ink/40 transition-colors text-text2 hover:text-ink"
                >
                  .md
                </button>
                <button
                  onClick={downloadDocx}
                  className="text-[11px] font-medium px-3 py-1.5 rounded-md border border-border hover:border-ink/40 transition-colors text-text2 hover:text-ink"
                >
                  .docx
                </button>
                <button
                  onClick={downloadPdf}
                  className="text-[11px] font-medium px-3 py-1.5 rounded-md border border-border hover:border-ink/40 transition-colors text-text2 hover:text-ink"
                >
                  .pdf
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="ml-1 w-8 h-8 grid place-items-center rounded-md hover:bg-ink/[0.06] text-text2 hover:text-ink transition-colors"
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-8 py-7 flex-1">
          {generating && (
            <div className="flex flex-col items-center gap-4 py-12 text-text2">
              <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
              <span className="text-sm">Synthesizing the {ASSET_LABEL[generating].toLowerCase()}…</span>
              <span className="text-xs text-text3 max-w-xs text-center leading-relaxed">
                Element IQ is reading the thread + brains and producing a structured artifact. Usually 5–15 seconds.
              </span>
            </div>
          )}

          {error && (
            <div className="px-4 py-3 bg-card border border-accent/40 rounded-md text-sm text-ink">
              <div className="font-display text-[10px] text-accent uppercase tracking-wide14 mb-1">Error</div>
              {error}
            </div>
          )}

          {asset && <Markdown text={asset.content_markdown} />}
        </div>
      </div>
    </div>
  );
}

// ─── helpers ───────────────────────────────────────────────────────

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function slugifyTitle(title: string): string {
  const base = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
  return base || 'element-iq-asset';
}

// Lightweight Markdown → HTML for export. Mirrors the in-app renderer's structure
// but inlines minimal styles so Word + print produce a clean document.
function markdownToHtml(src: string): string {
  const lines = src.split('\n');
  const out: string[] = [];
  let i = 0;
  const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const inline = (s: string) => {
    let r = escape(s);
    r = r.replace(/\[([\d,\s]+)\]/g, '<sup style="color:#ff2b0a;font-weight:600;">[$1]</sup>');
    r = r.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    r = r.replace(/(?<![*\w])\*([^*]+)\*(?!\w)/g, '<em>$1</em>');
    r = r.replace(/`([^`]+)`/g, '<code style="background:#f0efed;padding:1px 4px;border-radius:3px;font-family:Consolas,monospace;">$1</code>');
    return r;
  };
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*(?:-\s*-\s*-+|\*\s*\*\s*\*+|_\s*_\s*_+)\s*$/.test(line)) {
      out.push('<hr style="border:0;border-top:1px solid #ddd;margin:18px 0;" />'); i++; continue;
    }
    if (/^>\s?/.test(line)) {
      const q: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { q.push(lines[i].replace(/^>\s?/, '')); i++; }
      out.push(`<blockquote style="margin:10px 0;padding:6px 14px;border-left:3px solid #ff2b0a;background:#fafaf9;color:#444;font-style:italic;">${inline(q.join(' '))}</blockquote>`);
      continue;
    }
    if (line.startsWith('```')) {
      const code: string[] = []; i++;
      while (i < lines.length && !lines[i].startsWith('```')) { code.push(lines[i]); i++; }
      i++;
      out.push(`<pre style="background:#1d1d1d;color:#f2f2f2;border-radius:6px;padding:10px 14px;font-family:Consolas,monospace;font-size:13px;overflow-x:auto;"><code>${escape(code.join('\n'))}</code></pre>`);
      continue;
    }
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?\s*[-:]+/.test(lines[i + 1])) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes('|')) {
        const t = lines[i].trim();
        if (/^\s*\|?\s*[-:]+/.test(t)) { i++; continue; }
        rows.push(t.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim()));
        i++;
      }
      if (rows.length >= 1) {
        const [head, ...body] = rows;
        const thead = `<thead style="background:#fafaf9;"><tr>${head.map((c) => `<th style="padding:8px 12px;text-align:left;border:1px solid #d4d4d4;font-weight:600;font-size:12px;text-transform:uppercase;letter-spacing:0.04em;color:#444;">${inline(c)}</th>`).join('')}</tr></thead>`;
        const tbody = `<tbody>${body.map((r) => `<tr>${r.map((c) => `<td style="padding:8px 12px;border:1px solid #ececec;vertical-align:top;">${inline(c)}</td>`).join('')}</tr>`).join('')}</tbody>`;
        out.push(`<table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:14px;border:1px solid #d4d4d4;">${thead}${tbody}</table>`);
      }
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.+)/);
    if (h) {
      const lvl = h[1].length;
      const sizes = [0, 22, 18, 16, 14];
      out.push(`<h${lvl} style="font-family:Helvetica,Arial,sans-serif;font-weight:600;margin:18px 0 8px 0;font-size:${sizes[lvl]}px;color:#1d1d1d;">${inline(h[2])}</h${lvl}>`);
      i++; continue;
    }
    if (/^[-*]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) { items.push(lines[i].replace(/^[-*]\s/, '')); i++; }
      out.push(`<ul style="padding-left:24px;margin:8px 0;">${items.map((it) => `<li style="margin:4px 0;">${inline(it)}</li>`).join('')}</ul>`);
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s/, '')); i++; }
      out.push(`<ol style="padding-left:24px;margin:8px 0;">${items.map((it) => `<li style="margin:4px 0;">${inline(it)}</li>`).join('')}</ol>`);
      continue;
    }
    if (line.trim() === '') { i++; continue; }
    const para: string[] = [line]; i++;
    while (
      i < lines.length && lines[i].trim() !== ''
      && !lines[i].startsWith('```')
      && !lines[i].match(/^#{1,4}\s/)
      && !/^[-*]\s/.test(lines[i])
      && !/^\d+\.\s/.test(lines[i])
      && !(lines[i].includes('|') && i + 1 < lines.length && /^\s*\|?\s*[-:]+/.test(lines[i + 1]))
    ) { para.push(lines[i]); i++; }
    out.push(`<p style="margin:10px 0;line-height:1.55;">${inline(para.join(' '))}</p>`);
  }
  return out.join('\n');
}

function renderMarkdownForDocx(markdown: string, title: string): string {
  const body = markdownToHtml(markdown);
  return `<html xmlns:o='urn:schemas-microsoft-com:office:office'
xmlns:w='urn:schemas-microsoft-com:office:word'
xmlns='http://www.w3.org/TR/REC-html40'>
<head><meta charset="utf-8"><title>${escapeHtml(title)}</title></head>
<body style="font-family:Calibri,Helvetica,Arial,sans-serif;font-size:14px;color:#1d1d1d;">
${body}
</body></html>`;
}

function renderMarkdownForPrint(markdown: string, title: string): string {
  const body = markdownToHtml(markdown);
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  @page { margin: 0.75in; }
  body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; color: #1d1d1d; max-width: 7in; margin: 0 auto; }
  h1, h2, h3, h4 { color: #1d1d1d; }
  table { page-break-inside: avoid; }
  pre { white-space: pre-wrap; word-wrap: break-word; }
</style>
</head><body>
<h1 style="font-size:24px;border-bottom:1px solid #ddd;padding-bottom:8px;margin-bottom:14px;">${escapeHtml(title)}</h1>
${body}
</body></html>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
